import React, { FC, MouseEvent, memo, useCallback } from 'react';

import { Icon } from '@rbilabs/components-library/build/components/icon';
import { noop } from 'lodash';
import { useIntl } from 'react-intl';

import { FavoriteIcon } from 'components/favorite-icon';
import { ButtonForIconFav } from 'components/favorite-icon/styled';
import { FavoriteLoading } from 'components/lottie-animations/favorite-loading/favorite-loading';
import { OfferBadges } from 'components/offer-badges';
import { Picture } from 'components/picture';
import { useEnableOffersBadge } from 'hooks/use-enable-offers-badge';
import { useFeatureInvitationCode } from 'hooks/use-feature-invitation-code';
import { OfferStatus } from 'pages/loyalty/loyalty-offers/types';
import { OfferLocked } from 'pages/loyalty/offer-locked/offer-locked';
import { UpcomingOffer } from 'pages/loyalty/upcoming-offer/upcoming-offer';
import { IncentiveSource } from 'state/loyalty/types';

import { ILoyaltyRewardsViewProps } from '../../loyalty-rewards/types';
import { incentiveCardImageDefaultProps } from '../constants';
import { ParsedRewardData } from '../incentive-details/types';

import {
  BackgroundImage,
  CardContainer,
  DefaultBackground,
  ImageWrapper,
  IncentiveCardDescription,
  InfoContainer,
  LoadingWrapper,
  RewardText,
  StyledBadge,
  StyledClickableContainer,
  StyledRewardLock,
  StyledUpcomingOfferBadge,
  WrapperButton,
} from './incentive-card.styled';

export interface IIncentiveCard {
  incentiveData: ParsedRewardData;
  handleClick: ILoyaltyRewardsViewProps['handleClick'];
  children?: React.ReactNode;
  locked?: boolean;
  favorited?: boolean;
  favoriteEnabled?: boolean;
  favoriteLoading?: boolean;
  handleToggleFavorite?: (sanityId: string, name: string) => void;
  isQuestChallengeIcentive?: boolean;
  isUpcomingOffer?: boolean;
}

export const IncentiveCard: FC<IIncentiveCard> = memo(
  ({
    incentiveData,
    handleClick,
    children,
    locked = false,
    favorited = false,
    favoriteEnabled = false,
    favoriteLoading = false,
    handleToggleFavorite = noop,
    isQuestChallengeIcentive = false,
    isUpcomingOffer = false,
  }) => {
    const { formatMessage } = useIntl();
    const {
      image,
      imageDescription,
      name,
      id,
      loyaltyEngineId,
      backgroundImage,
      metadata,
      incentiveEndingIn,
    } = incentiveData;

    const { featureInvitationCode } = useFeatureInvitationCode();
    const questBenefit =
      featureInvitationCode?.invitationCodeBenefit &&
      featureInvitationCode?.invitationCodeBenefit[0];

    const incentiveDetails = {
      name: isQuestChallengeIcentive ? questBenefit?.name?.locale : name,
      description: isQuestChallengeIcentive ? questBenefit?.howToRedeem?.localeRaw : '',
      image: isQuestChallengeIcentive ? questBenefit?.image?.locale : image,
      backgroundImage: <DefaultBackground />,
    };

    const { enableOfferBadges } = useEnableOffersBadge();

    // Offers will use the EngineId as the URL identifier because the SanityId might be the same for multiple PersonalizedOffers.
    // Rewards will use the SanityId until we are sure we don't have any deep links to specific Rewards.
    const incentiveSanityId = id;
    const incentiveName = name;

    const showPromoCodeLabel = metadata?.some(
      data => data?.value === IncentiveSource.PROMOTION_CODE
    );
    const promoCodeLabel = showPromoCodeLabel
      ? formatMessage({ id: 'promoCodeBadgeLabel' })
      : undefined;

    const handleFavoriteClick = useCallback(
      (event: MouseEvent<HTMLElement>) => {
        event.preventDefault();
        event.stopPropagation();
        handleToggleFavorite(id, name);
      },
      [handleToggleFavorite, id, name]
    );

    let status: OfferStatus = OfferStatus.Available;
    if (locked) {
      status = OfferStatus.Locked;
    }
    if (isUpcomingOffer) {
      status = OfferStatus.ComingSoon;
    }

    return (
      <CardContainer data-testid={`incentives-list-${locked ? 'locked-offer' : 'reward-item'}`}>
        <StyledClickableContainer
          onClick={handleClick({
            incentiveName,
            loyaltyEngineId,
            incentiveSanityId,
            isQuestChallengeIcentive,
            isUpcomingOffer,
            status,
          })}
          isLocked={locked}
          isQuestChallengeIcentive={isQuestChallengeIcentive}
        >
          {incentiveDetails.image && (
            <ImageWrapper>
              {locked && !isUpcomingOffer && <StyledRewardLock />}
              {isQuestChallengeIcentive && <OfferLocked />}
              {isUpcomingOffer && <UpcomingOffer />}

              {isQuestChallengeIcentive
                ? incentiveDetails.backgroundImage
                : backgroundImage && <BackgroundImage alt={name} image={backgroundImage} />}
              <Picture
                image={incentiveDetails.image}
                alt={imageDescription}
                {...incentiveCardImageDefaultProps}
              />
            </ImageWrapper>
          )}

          <InfoContainer>
            <RewardText data-testid="incentive-name">{incentiveDetails.name}</RewardText>
            {isQuestChallengeIcentive ? (
              <IncentiveCardDescription content={incentiveDetails.description} />
            ) : (
              children
            )}
            {promoCodeLabel && (
              <StyledBadge>
                <Icon icon="offer" color="black" width="18px" height="18px" />
                {promoCodeLabel}
              </StyledBadge>
            )}
            {!isUpcomingOffer && enableOfferBadges && (
              <OfferBadges displayLoginRequiredBadge={false} endingIn={incentiveEndingIn} />
            )}
            {isUpcomingOffer && <StyledUpcomingOfferBadge />}
          </InfoContainer>
          {favoriteEnabled && (
            <WrapperButton>
              <ButtonForIconFav
                data-testid={favorited ? 'fav-icon-selected' : 'fav-icon'}
                onClick={handleFavoriteClick}
                aria-label={formatMessage({
                  id: favorited ? 'removeRestaurantFromFavorites' : 'setRestaurantAsFavorite',
                })}
                isTransparent={true}
              >
                {favoriteLoading ? (
                  <LoadingWrapper>
                    <FavoriteLoading />
                  </LoadingWrapper>
                ) : (
                  <FavoriteIcon isFavorite={favorited} />
                )}
              </ButtonForIconFav>
            </WrapperButton>
          )}
        </StyledClickableContainer>
      </CardContainer>
    );
  }
);
